import { useState, useCallback, useRef, useEffect } from 'react';
import { Menu, Bell, AlertTriangle, CreditCard, Clock } from 'lucide-react';
import { useNavigate, Link } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import HelpPanel from './HelpPanel';
import CommandPalette from './CommandPalette';
import ShortcutsModal from './ShortcutsModal';
import QuickAddFAB from './QuickAddFAB';
import LeadFormModal from './LeadFormModal';
import FunnelPickerModal from './FunnelPickerModal';
import { useSSENotifications } from '../hooks/useSSENotifications';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { api } from '../services/api';
import OnboardingTour from './OnboardingTour';
import { usePushNotifications } from '../hooks/usePushNotifications';

function NotificationToast({ notifications, onDismiss }) {
  if (!notifications.length) return null;
  const latest = notifications[0];
  return (
    <div className="fixed z-50 max-w-sm w-full pointer-events-none top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))]">
      <div
        className="bg-[#002233] border border-[#00DBFF]/30 rounded-lg shadow-xl p-4 flex items-start gap-3 pointer-events-auto"
        role="alert"
      >
        <div className="shrink-0 w-8 h-8 rounded-full bg-[#00DBFF]/10 flex items-center justify-center">
          <Bell size={14} className="text-[#00DBFF]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-white/90 mb-0.5">{latest.title || 'Nova notificação'}</p>
          <p className="text-[11px] text-white/55 truncate">{latest.message || ''}</p>
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 text-white/30 hover:text-white/70 text-lg leading-none transition-colors"
          aria-label="Fechar"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function SubscriptionBanner() {
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    fetch('/api/billing/status', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => setBilling(data))
      .catch(() => {});
  }, []);

  if (!billing) return null;

  const { status, trial_ends_at, plano } = billing;
  if (status === 'active' && plano !== 'trial') return null;

  const daysLeft = trial_ends_at
    ? Math.max(0, Math.ceil((new Date(trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  if (status === 'trialing' && daysLeft !== null) {
    return (
      <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 text-yellow-300 text-sm">
          <Clock size={14} />
          <span>Trial: <strong>{daysLeft} dia{daysLeft !== 1 ? 's' : ''}</strong> restante{daysLeft !== 1 ? 's' : ''}. Ative seu plano para continuar.</span>
        </div>
        <Link to="/billing" className="text-xs bg-yellow-500 text-[#002233] font-bold px-3 py-1 rounded-lg hover:bg-yellow-400 transition-colors whitespace-nowrap ml-4">
          Ativar plano
        </Link>
      </div>
    );
  }

  if (status === 'past_due') {
    return (
      <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 text-red-300 text-sm">
          <AlertTriangle size={14} />
          <span>Pagamento pendente. Atualize seu método de pagamento para evitar suspensão.</span>
        </div>
        <Link to="/billing" className="text-xs bg-red-500 text-white font-bold px-3 py-1 rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap ml-4">
          Regularizar
        </Link>
      </div>
    );
  }

  return null;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

function loadSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(loadSidebarCollapsed);

  // ZYN-328: modo reduzido (só ícones) — persiste entre reloads/sessões
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0'); } catch { /* quota — ignore */ }
  }, [sidebarCollapsed]);

  const toggleSidebarCollapsed = useCallback(() => setSidebarCollapsed(v => !v), []);
  const [helpOpen, setHelpOpen] = useState(false);
  const [toastItems, setToastItems] = useState([]);
  const prevUnreadRef = useRef(null);
  const toastTimer = useRef(null);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [funnelPickerOpen, setFunnelPickerOpen] = useState(false);
  const [pendingFunnel, setPendingFunnel] = useState('new_names');
  const [fabEsns, setFabEsns] = useState([]);
  const [fabProducts, setFabProducts] = useState([]);
  const [fabContas, setFabContas] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { user } = useAuth();

  // ZYN-64: Registra subscription de push após login
  usePushNotifications(user);

  // ZYN-69: Onboarding — check if user needs the tour
  useEffect(() => {
    if (!user) return;
    api.getOnboardingStatus().then(d => {
      if (!d.onboarding_completed) {
        // Small delay to let UI settle before showing tour
        const t = setTimeout(() => setShowOnboarding(true), 1200);
        return () => clearTimeout(t);
      }
    }).catch(() => {});
  }, [user?.id]);

  // Quando o usuário aciona "+ Novo Lead" sem contexto de funil (sidebar/CommandPalette/FAB),
  // abrimos primeiro o picker para evitar criar oportunidade no funil errado.
  function requestNewLead() {
    setFunnelPickerOpen(true);
  }
  function handleFunnelPicked(funnel) {
    setPendingFunnel(funnel);
    setFunnelPickerOpen(false);
    setNewLeadOpen(true);
  }

  // ZYN-308: outras telas (ex.: toast pós-criação de conta) podem pedir o fluxo
  // de novo lead com uma conta pré-selecionada via evento global.
  const [pendingConta, setPendingConta] = useState(null);
  useEffect(() => {
    function onRequestNewLead(e) {
      setPendingConta(e.detail?.conta || null);
      requestNewLead();
    }
    window.addEventListener('crm:request-new-lead', onRequestNewLead);
    return () => window.removeEventListener('crm:request-new-lead', onRequestNewLead);
  }, []);

  useEffect(() => {
    if (!newLeadOpen) return;
    Promise.all([api.getEsns(), api.getProducts(), api.getContas({ limit: 500, autocomplete: 1 })]).then(([esns, products, contasRes]) => {
      setFabEsns(Array.isArray(esns) ? esns : []);
      setFabProducts(Array.isArray(products) ? products : []);
      setFabContas(contasRes?.data ?? (Array.isArray(contasRes) ? contasRes : []));
    }).catch(() => {});
  }, [newLeadOpen]);

  useKeyboardShortcuts({
    onPalette: () => setPaletteOpen(true),
    onShortcuts: () => setShortcutsOpen(true),
    onToggleSidebar: toggleSidebarCollapsed,
  });

  const handleSSEUpdate = useCallback(({ items, total_unread }) => {
    if (prevUnreadRef.current !== null && total_unread > prevUnreadRef.current && items.length > 0) {
      setToastItems(items.slice(0, 1));
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToastItems([]), 5000);
    }
    prevUnreadRef.current = total_unread;
  }, []);

  useSSENotifications(handleSSEUpdate);

  return (
    /* h-[100dvh] evita "100vh" cortar com barra do Safari; safe-area com viewport-fit=cover */
    <div className="flex h-[100dvh] min-h-0 overflow-hidden bg-[#f0f2f5] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      {/* Skip-to-content: visível apenas no foco do teclado (acessibilidade) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-[#002233] focus:text-white focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Ir para o conteúdo principal
      </a>
      {/* Desktop sidebar — fixed height, never scrolls.
          O wrapper reserva a largura "pinada" (64px reduzido / 240px expandido)
          no fluxo flex; o <aside> em si é posicionado por cima dele (absolute)
          e pode crescer visualmente ao hover sem empurrar o conteúdo (ZYN-329). */}
      <div className={`hidden md:block relative flex-shrink-0 transition-[width] duration-200 ${sidebarCollapsed ? 'w-16' : 'w-60'}`}>
        <Sidebar
          onPaletteOpen={() => setPaletteOpen(true)}
          onNewLead={requestNewLead}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
        />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div className={`fixed inset-y-0 left-0 z-50 md:hidden transition-transform duration-300 pl-[env(safe-area-inset-left)] ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <Sidebar
          onMobileClose={() => setSidebarOpen(false)}
          onPaletteOpen={() => setPaletteOpen(true)}
          onNewLead={requestNewLead}
        />
      </div>

      {/* Main content — column layout, header fixed + main scrolls */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center gap-3 px-4 pt-[max(0.625rem,env(safe-area-inset-top))] pb-2.5 bg-[#002233] border-b border-white/10 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white/70 hover:text-white transition-colors"
          >
            <Menu size={20} />
          </button>
          <span className="text-white text-sm font-semibold">CRM TOTVS Moda</span>
        </div>

        {/* Desktop TopBar — flex-shrink-0 keeps it from compressing */}
        <div className="hidden md:block flex-shrink-0">
          <TopBar
            onHelpToggle={() => setHelpOpen(o => !o)}
            helpOpen={helpOpen}
          />
        </div>

        {/* Subscription status banner */}
        <SubscriptionBanner />

        {/* Scrollable content area */}
        <main id="main-content" className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Context-sensitive Help Panel */}
      <HelpPanel isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Toast de nova notificação */}
      <NotificationToast
        notifications={toastItems}
        onDismiss={() => {
          clearTimeout(toastTimer.current);
          setToastItems([]);
        }}
      />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNewLead={() => { setPaletteOpen(false); requestNewLead(); }}
        onNewConta={() => { setPaletteOpen(false); navigate('/contas?new=1'); }}
        onShowShortcuts={() => setShortcutsOpen(true)}
      />

      <ShortcutsModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      <QuickAddFAB
        onNewLead={requestNewLead}
        onNewConta={() => navigate('/contas?new=1')}
      />

      <FunnelPickerModal
        isOpen={funnelPickerOpen}
        onClose={() => setFunnelPickerOpen(false)}
        onPick={handleFunnelPicked}
      />

      <LeadFormModal
        isOpen={newLeadOpen}
        onClose={() => { setNewLeadOpen(false); setPendingConta(null); }}
        initialConta={pendingConta}
        onSave={async (formData) => {
          // Persiste de verdade. handleSubmit do modal já valida + faz try/catch;
          // qualquer erro (incl. 409 dup CNPJ) é exibido pelo banner do form.
          const created = await api.createLead({
            ...formData,
            funnel_type: formData?.funnel_type || pendingFunnel,
          });
          setNewLeadOpen(false);
          setPendingConta(null);
          // Sinaliza para a FunnelPage atual recarregar — cobre o caso em que
          // o usuário já está no funil de destino (navigate é no-op e
          // useEffect[loadData] não re-dispara). FunnelPage escuta este evento.
          window.dispatchEvent(new CustomEvent('crm:lead-created', { detail: created }));
          // Leva o usuário ao funil onde o lead foi parar, com o lead aberto.
          // Rotas de funil são /new-names e /base (não existe /funnel/*) — ZYN-303.
          const funnelPath = created.funnel_type === 'base' ? '/base' : '/new-names';
          navigate(`${funnelPath}?openLead=${created.id}`);
          return created.id;
        }}
        funnelType={pendingFunnel}
        esns={fabEsns}
        products={fabProducts}
        contas={fabContas}
        leads={[]}
        onCreateEsn={() => {}}
        onCreateProduct={() => {}}
      />

      {showOnboarding && (
        <OnboardingTour onComplete={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}
